"use client"

import React, { useState, useRef, useEffect } from 'react'
import { cn } from '../../lib/utils'
import { GripVertical, Move } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSubscription } from '../../hooks/use-subscription'
import { ProLockScreen } from '../shared/pro-lock-screen'

export interface DraggableListProps<T> {
  items: T[]
  onReorder: (items: T[]) => void
  keyExtractor: (item: T) => string
  renderItem: (item: T, index: number) => React.ReactNode
  className?: string
  itemClassName?: string
  dragHandleClassName?: string
  showDragHandle?: boolean
  dragHandleSize?: number
  disabled?: boolean
  onDragStart?: (item: T, index: number) => void
  onDragEnd?: (item: T, index: number) => void
  gap?: number
  animationDuration?: number
  showDropIndicator?: boolean
  dropIndicatorColor?: string
  dropIndicatorStyle?: 'line' | 'area' | 'glow'
  dragPreviewStyle?: 'ghost' | 'clone' | 'compact'
  hapticFeedback?: boolean
}

export function DraggableList<T>({
  items,
  onReorder,
  keyExtractor,
  renderItem,
  className,
  itemClassName,
  dragHandleClassName,
  showDragHandle = false,
  dragHandleSize = 16,
  disabled = false,
  onDragStart,
  onDragEnd,
  gap = 8,
  animationDuration = 200,
  showDropIndicator = true,
  dropIndicatorColor = 'primary',
  dropIndicatorStyle = 'glow',
  dragPreviewStyle = 'clone',
  hapticFeedback = true
}: DraggableListProps<T>) {
  const { hasProAccess, isLoading } = useSubscription();
  
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dropPosition, setDropPosition] = useState<{ index: number; position: 'before' | 'after' } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isOverDropZone, setIsOverDropZone] = useState(false)
  const draggedItem = useRef<T | null>(null)
  const dragCounter = useRef(0)
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (disabled) return
    
    const item = items[index]
    draggedItem.current = item
    setDraggedIndex(index)
    setIsDragging(true)
    
    // Calculate drag offset for smooth positioning
    const rect = e.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
    
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', '')
    
    // Create enhanced drag preview
    if (e.currentTarget instanceof HTMLElement) {
      const dragImage = e.currentTarget.cloneNode(true) as HTMLElement
      dragImage.style.position = 'absolute'
      dragImage.style.top = '-1000px'
      dragImage.style.width = `${rect.width}px`
      
      if (dragPreviewStyle === 'ghost') {
        dragImage.style.opacity = '0.5'
        dragImage.style.filter = 'grayscale(100%)'
      } else if (dragPreviewStyle === 'clone') {
        dragImage.style.opacity = '0.9'
        dragImage.style.transform = 'scale(1.02) rotate(1deg)'
        dragImage.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)'
      } else if (dragPreviewStyle === 'compact') {
        dragImage.style.transform = 'scale(0.95)'
        dragImage.style.opacity = '0.85'
      }
      
      dragImage.style.pointerEvents = 'none'
      document.body.appendChild(dragImage)
      e.dataTransfer.setDragImage(dragImage, dragOffset.x, dragOffset.y)
      setTimeout(() => document.body.removeChild(dragImage), 0)
    }
    
    // Haptic feedback for mobile devices
    if (hapticFeedback && 'vibrate' in navigator) {
      navigator.vibrate(10)
    }
    
    onDragStart?.(item, index)
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current++
  }

  const handleDragLeave = (e: React.DragEvent) => {
    dragCounter.current--
    if (dragCounter.current === 0) {
      setDropPosition(null)
    }
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    if (disabled || draggedIndex === null) return
    
    // Skip if hovering over the dragged item itself
    if (index === draggedIndex) {
      setDropPosition(null)
      setIsOverDropZone(false)
      return
    }
    
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const height = rect.height
    
    // Determine drop position based on mouse position
    const position: 'before' | 'after' = y < height / 2 ? 'before' : 'after'
    
    setDropPosition({ index, position })
    setIsOverDropZone(true)
    
    // Visual feedback with haptics
    if (hapticFeedback && 'vibrate' in navigator) {
      navigator.vibrate(5)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    
    if (disabled || draggedIndex === null || !dropPosition || draggedItem.current === null) {
      handleDragEnd()
      return
    }
    
    const newItems = [...items]
    const draggedItemContent = newItems[draggedIndex]
    
    // Calculate target index before removing the item
    let targetIndex = dropPosition.index
    
    // Adjust target index based on position
    if (dropPosition.position === 'after') {
      targetIndex = targetIndex + 1
    }
    
    // Remove from old position
    newItems.splice(draggedIndex, 1)
    
    // Adjust target index if we removed an item before it
    if (draggedIndex < targetIndex) {
      targetIndex = targetIndex - 1
    }
    
    // Insert at new position
    newItems.splice(targetIndex, 0, draggedItemContent)
    
    onReorder(newItems)
    handleDragEnd()
  }

  const handleDragEnd = () => {
    if (draggedItem.current && draggedIndex !== null) {
      onDragEnd?.(draggedItem.current, draggedIndex)
    }
    
    // Smooth transition out
    setTimeout(() => {
      setDraggedIndex(null)
      setDropPosition(null)
      setIsDragging(false)
      setIsOverDropZone(false)
      draggedItem.current = null
      dragCounter.current = 0
    }, 50)
    
    // Final haptic feedback
    if (hapticFeedback && 'vibrate' in navigator) {
      navigator.vibrate([10, 50, 10])
    }
  }

  // Render drop indicator based on style
  const renderDropIndicator = (position: 'before' | 'after') => {
    if (!showDropIndicator || !isDragging) return null
    
    const positionClasses = position === 'before' 
      ? '-top-1' 
      : '-bottom-1'
    
    if (dropIndicatorStyle === 'line') {
      return (
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          exit={{ opacity: 0, scaleX: 0 }}
          className={cn(
            "absolute left-0 right-0 h-1 z-50 pointer-events-none",
            positionClasses
          )}
        >
          <div className="h-full bg-primary rounded-full relative">
            <motion.div
              animate={{ x: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full"
            />
            <motion.div
              animate={{ x: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full"
            />
          </div>
        </motion.div>
      )
    }
    
    if (dropIndicatorStyle === 'area') {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 24 }}
          exit={{ opacity: 0, height: 0 }}
          className={cn(
            "absolute left-0 right-0 z-40 pointer-events-none",
            position === 'before' ? '-top-3' : '-bottom-3'
          )}
        >
          <div className="h-full bg-primary/10 rounded-lg border-2 border-dashed border-primary/50" />
        </motion.div>
      )
    }
    
    if (dropIndicatorStyle === 'glow') {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className={cn(
            "absolute left-0 right-0 h-2 z-50 pointer-events-none",
            positionClasses
          )}
        >
          <div className="h-full relative">
            <div className="absolute inset-0 bg-primary/30 blur-xl animate-pulse" />
            <div className="absolute inset-0 bg-primary rounded-full animate-pulse" />
            <motion.div
              animate={{ 
                opacity: [0.5, 1, 0.5],
                scale: [0.95, 1.05, 0.95]
              }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="absolute inset-0 bg-gradient-to-r from-primary via-primary/50 to-primary rounded-full"
            />
          </div>
        </motion.div>
      )
    }
    
    return null
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="Draggable List" 
        compact={true}
      />
    );
  }

  return (
    <div 
      className={cn(
        "relative",
        isDragging && "select-none",
        className
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <AnimatePresence>
        {items.map((item, index) => {
          const key = keyExtractor(item)
          const isBeingDragged = draggedIndex === index
          const showDropBefore = dropPosition?.index === index && dropPosition?.position === 'before'
          const showDropAfter = dropPosition?.index === index && dropPosition?.position === 'after'
          
          return (
            <motion.div
              key={key}
              ref={(el) => {
                if (el) itemRefs.current.set(key, el)
              }}
              layout
              layoutId={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: isBeingDragged ? 0.3 : 1,
                y: 0,
                scale: isBeingDragged ? 0.95 : 1,
                rotate: isBeingDragged ? 2 : 0
              }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ 
                type: "spring",
                stiffness: 500,
                damping: 30,
                duration: animationDuration / 1000
              }}
              className="relative"
              style={{ marginBottom: gap }}
            >
              {/* Drop indicator before */}
              {showDropBefore && renderDropIndicator('before')}
              
              <div
                draggable={!disabled}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={cn(
                  "relative bg-card rounded-lg border transition-all",
                  isBeingDragged && "cursor-grabbing shadow-2xl",
                  !isBeingDragged && isDragging && "hover:shadow-lg hover:border-primary/30",
                  !isBeingDragged && !isDragging && !disabled && "hover:shadow-md hover:border-border/80",
                  !disabled && "cursor-grab active:cursor-grabbing",
                  showDropBefore && "translate-y-3",
                  showDropAfter && "-translate-y-3",
                  itemClassName
                )}
              >
                <div className="flex items-center gap-3 p-2">
                  {showDragHandle && (
                    <motion.div 
                      className="pl-2"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <GripVertical 
                        className={cn(
                          "flex-shrink-0 text-muted-foreground/50",
                          !disabled && "hover:text-muted-foreground",
                          "transition-colors",
                          dragHandleClassName
                        )}
                        size={dragHandleSize}
                      />
                    </motion.div>
                  )}
                  <div className="flex-1">
                    {renderItem(item, index)}
                  </div>
                </div>
              </div>
              
              {/* Drop indicator after */}
              {showDropAfter && renderDropIndicator('after')}
            </motion.div>
          )
        })}
      </AnimatePresence>
      
      {/* Global drag overlay */}
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 pointer-events-none z-[100]"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
        </motion.div>
      )}
    </div>
  )
}

// Export the component
export default DraggableList